import React, { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { BookOpen, ExternalLink, Star, ArrowRight, ScrollText, Users, Drama, Landmark, GraduationCap, HeartPulse, Briefcase, MapPin, Trees, Map as MapIcon, FlaskConical, Wheat } from "lucide-react";
import client from "@/lib/api";
import { T, useLanguage } from "@/context/LanguageContext";
import { useTTS } from "@/context/TTSContext";
import { InlineListen } from "@/components/site/ListenControl";
import { Seo } from "@/components/site/Seo";
import { SectionHeading } from "@/components/home/ExploreByState";

const ICONS = { "scroll-text": ScrollText, users: Users, drama: Drama, landmark: Landmark, "graduation-cap": GraduationCap, "heart-pulse": HeartPulse, briefcase: Briefcase, "map-pin": MapPin, trees: Trees, map: MapIcon, "flask-conical": FlaskConical, wheat: Wheat };

export default function VerticalPage() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [state, setState] = useState("loading");
  const { lang, dict } = useLanguage();
  const tts = useTTS();

  useEffect(() => {
    setState("loading"); setData(null);
    window.scrollTo(0, 0);
    client.get(`/vertical/${slug}`)
      .then((r) => { setData(r.data); setState("ok"); })
      .catch(() => setState("empty"));
  }, [slug]);

  const readable = useMemo(() => {
    if (!data) return [];
    const src = [data.intro, ...(data.sections || []).flatMap((s) => [s.heading, s.body])].filter(Boolean);
    return lang.code === "en" ? src : src.map((t) => dict[t] || t);
  }, [data, lang.code, dict]);

  useEffect(() => { if (data && tts) tts.setContent(readable); }, [data, readable, tts]);

  if (state === "loading") return <div className="mx-auto max-w-[1100px] px-4 py-16"><div className="h-96 animate-pulse rounded-xl bg-parchment" /></div>;

  if (state === "empty") {
    const title = slug.charAt(0).toUpperCase() + slug.slice(1);
    return (
      <div className="mx-auto max-w-[1100px] px-4 py-20 text-center">
        <h1 className="font-display text-4xl font-bold text-navy">{title} <T>Pulse</T></h1>
        <p className="mt-4 text-muted-foreground"><T>Content for this portal is being generated and will appear shortly.</T></p>
        <Link to="/explore/states" className="mt-6 inline-flex items-center gap-2 rounded-md bg-navy px-6 py-3 font-semibold text-white"><T>Explore the geographic network</T> <ArrowRight className="h-4 w-4" /></Link>
      </div>
    );
  }

  const Icon = ICONS[data.icon] || Star;

  return (
    <div>
      <Seo
        title={data.title}
        path={`/vertical/${slug}`}
        type="article"
        description={data.intro}
      />
      <div className="relative overflow-hidden bg-navy text-white">
        <div className="absolute -right-24 -top-24 h-96 w-96 rounded-full bg-oldglory-red/20 blur-3xl" />
        <div className="relative mx-auto max-w-[1100px] px-4 py-14">
          <div className="mb-3 flex items-center gap-3">
            <span className="inline-flex rounded-lg bg-white/10 p-3"><Icon className="h-7 w-7 text-oldglory-red" /></span>
            <span className="text-xs font-bold uppercase tracking-[0.25em] text-oldglory-red"><T>USAPulse Vertical</T></span>
          </div>
          <h1 className="font-display text-5xl font-black md:text-6xl"><T>{data.title}</T></h1>
          <p className="mt-4 max-w-2xl text-lg text-white/80"><T>{data.intro}</T></p>
          <div className="mt-6 flex flex-wrap items-center gap-3">
            <InlineListen paragraphs={readable} label="Listen to this page" testid="vertical-listen-btn" />
            {data.wikipedia?.available && (
              <a href={data.wikipedia.wikipedia_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 rounded-full border border-white/25 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-white/10" data-testid="vertical-wikipedia-link">
                <BookOpen className="h-4 w-4 text-oldglory-red" /> <T>Learn more on Wikipedia</T> <ExternalLink className="h-3.5 w-3.5 opacity-60" />
              </a>
            )}
          </div>
        </div>
      </div>

      <div className="mx-auto max-w-[1100px] px-4 py-12">
        {data.known_for?.length > 0 && (
          <div className="mb-10">
            <SectionHeading kicker="Highlights" title="Known For" />
            <div className="-mt-4 flex flex-wrap gap-2.5">
              {data.known_for.map((k) => <span key={k} className="rounded-full bg-navy/5 px-4 py-2 text-sm font-medium text-navy ring-1 ring-navy/10"><T>{k}</T></span>)}
            </div>
          </div>
        )}

        <div className="space-y-8">
          {(data.sections || []).map((s, i) => (
            <motion.section
              key={i}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.5 }}
              className="border-l-2 border-oldglory-red/30 pl-5"
              data-testid={`vertical-section-${i}`}
            >
              <div className="mb-2 flex items-center justify-between gap-3">
                <h2 className="font-display text-2xl font-bold text-navy"><T>{s.heading}</T></h2>
                <InlineListen paragraphs={[lang.code === "en" ? s.body : dict[s.body] || s.body]} testid={`vertical-listen-${i}`} />
              </div>
              <p className="leading-relaxed text-ink/85"><T>{s.body}</T></p>
            </motion.section>
          ))}
        </div>

        <div className="mt-12 rounded-xl bg-parchment/50 p-6 text-sm text-muted-foreground">
          <T>Original USAPulse summary</T> · <T>Reviewed</T> {data.last_updated} · <T>Generated with AI, editorially structured</T>
        </div>
      </div>
    </div>
  );
}
